<template>
  <div class="date-input">
    <a-input
      ref="textInput"
      :value="displayValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :allow-clear="allowClear"
      :maxLength="10"
      autocomplete="off"
      @input="handleInput"
      @blur="commitValue"
      @keydown.native="handleKeydown"
      @clear="clearValue"
    >
      <a-date-picker
        slot="suffix"
        :value="momentValue"
        :format="displayFormat"
        :disabled="disabled"
        :disabled-date="disabledDateProxy"
        :default-picker-value="defaultPickerValueProxy"
        :show-today="false"
        :open="pickerOpen"
        class="date-input__picker"
        @openChange="handlePickerOpenChange"
        @change="handlePickerChange"
      >
        <a-icon
          slot="suffixIcon"
          type="calendar"
          class="date-input__calendar-icon"
          @click.stop="openPicker"
        />
      </a-date-picker>
    </a-input>

    <div v-if="errorMessage" class="date-input__error">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "DateInput",

  model: {
    prop: "value",
    event: "input",
  },

  props: {
    value: {
      type: [String, Object],
      default: null,
    },

    placeholder: {
      type: String,
      default: "dd/mm/yyyy",
    },

    displayFormat: {
      type: String,
      default: "DD/MM/YYYY",
    },

    valueFormat: {
      type: String,
      default: "YYYY-MM-DD",
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    allowClear: {
      type: Boolean,
      default: true,
    },

    minimumAge: {
      type: Number,
      default: 18,
    },

    maximumAge: {
      type: Number,
      default: 0,
    },

    disabledDate: {
      type: Function,
      default: null,
    },

    defaultPickerValue: {
      type: Object,
      default: null,
    },

    required: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      displayValue: "",
      momentValue: null,
      pickerOpen: false,
      errorMessage: "",
      isCommitting: false,
    };
  },

  computed: {
    defaultPickerValueProxy() {
      if (this.defaultPickerValue) {
        return this.defaultPickerValue;
      }

      if (this.minimumAge > 0) {
        return moment().subtract(this.minimumAge, "years");
      }

      if (this.maximumAge > 0) {
        return moment().subtract(this.maximumAge, "years");
      }

      return moment();
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(value) {
        if (this.isCommitting) {
          return;
        }

        this.syncFromValue(value);
      },
    },
  },

  beforeDestroy() {
    this.pickerOpen = false;
  },

  methods: {
    syncFromValue(value) {
      if (!value) {
        this.displayValue = "";
        this.momentValue = null;
        return;
      }

      let parsed = null;

      if (moment.isMoment(value)) {
        parsed = value.clone();
      } else {
        parsed = moment(
          value,
          [
            this.valueFormat,
            this.displayFormat,
            "DD-MM-YYYY",
            "DD.MM.YYYY",
            "DDMMYYYY",
          ],
          true,
        );
      }

      if (!parsed || !parsed.isValid()) {
        this.displayValue = "";
        this.momentValue = null;
        return;
      }

      this.momentValue = parsed;
      this.displayValue = parsed.format(this.displayFormat);
    },

    handleInput(event) {
      const rawValue =
        typeof event === "string"
          ? event
          : event && event.target
          ? event.target.value
          : "";

      this.displayValue = this.FormatDateInput(rawValue);
      this.errorMessage = "";

      if (!this.displayValue) {
        this.momentValue = null;
        this.emitValue(null);
      }
    },

    FormatDateInput(Value) {
      const Digits = String(Value || "")
        .replace(/\D/g, "")
        .slice(0, 8);

      if (!Digits) {
        return "";
      }

      if (Digits.length < 2) {
        return Digits;
      }

      if (Digits.length === 2) {
        return Digits + "/";
      }

      if (Digits.length < 4) {
        return Digits.slice(0, 2) + "/" + Digits.slice(2);
      }

      if (Digits.length === 4) {
        return Digits.slice(0, 2) + "/" + Digits.slice(2, 4) + "/";
      }

      return (
        Digits.slice(0, 2) + "/" + Digits.slice(2, 4) + "/" + Digits.slice(4, 8)
      );
    },

    handleKeydown(event) {
      if (event.key === "Enter") {
        event.preventDefault();
        this.commitValue();
        return;
      }

      if (event.key === "Escape") {
        this.pickerOpen = false;
        return;
      }

      if (event.key === "Tab") {
        // Không preventDefault để Tab chuyển focus tự nhiên.
        this.commitValue();
      }
    },

    commitValue() {
      if (this.isCommitting) {
        return;
      }

      const rawValue = String(this.displayValue || "").trim();

      if (!rawValue) {
        if (this.required) {
          this.setError("Vui lòng nhập ngày.", null);
          return;
        }

        this.clearValue();
        return;
      }

      const parsed = moment(
        rawValue,
        [this.displayFormat, "DD-MM-YYYY", "DD.MM.YYYY", "DDMMYYYY"],
        true,
      );

      if (!parsed.isValid()) {
        this.setError(
          "Ngày không hợp lệ. Vui lòng nhập theo định dạng DD/MM/YYYY.",
          rawValue,
        );
        return;
      }

      const ageValidation = this.validateAge(parsed);

      if (!ageValidation.State) {
        this.setError(ageValidation.Message, parsed);
        return;
      }

      if (
        typeof this.disabledDate === "function" &&
        this.disabledDate(parsed)
      ) {
        this.setError("Ngày đã chọn không hợp lệ.", parsed);
        return;
      }

      this.errorMessage = "";
      this.momentValue = parsed;
      this.displayValue = parsed.format(this.displayFormat);

      this.emitMoment(parsed);
    },

    validateAge(dateValue) {
      if (
        this.minimumAge > 0 &&
        dateValue.isAfter(moment().subtract(this.minimumAge, "years"), "day")
      ) {
        return {
          State: 0,
          Message: `Người tham gia phải đủ ${this.minimumAge} tuổi.`,
        };
      }

      if (
        this.maximumAge > 0 &&
        dateValue.isBefore(moment().subtract(this.maximumAge, "years"), "day")
      ) {
        return {
          State: 0,
          Message: `Người tham gia không được quá ${this.maximumAge} tuổi.`,
        };
      }

      return {
        State: 1,
        Message: "",
      };
    },

    handlePickerChange(value) {
      if (!value) {
        this.clearValue();
        return;
      }

      const ageValidation = this.validateAge(value);

      if (!ageValidation.State) {
        this.setError(ageValidation.Message, value);
        return;
      }

      if (typeof this.disabledDate === "function" && this.disabledDate(value)) {
        this.setError("Ngày đã chọn không hợp lệ.", value);
        return;
      }

      this.errorMessage = "";
      this.momentValue = value;
      this.displayValue = value.format(this.displayFormat);
      this.pickerOpen = false;

      this.emitMoment(value);

      this.$nextTick(() => {
        this.focus();
      });
    },

    openPicker() {
      if (this.disabled) {
        return;
      }

      this.pickerOpen = true;
    },

    handlePickerOpenChange(open) {
      this.pickerOpen = open;
    },

    disabledDateProxy(current) {
      if (!current) {
        return false;
      }

      const ageValidation = this.validateAge(current);

      if (!ageValidation.State) {
        return true;
      }

      if (
        typeof this.disabledDate === "function" &&
        this.disabledDate(current)
      ) {
        return true;
      }

      return false;
    },

    setError(message, data) {
      this.errorMessage = message;

      this.$emit("error", {
        State: 0,
        Message: message,
        Data: data,
      });
    },

    emitMoment(value) {
      const output = this.valueFormat ? value.format(this.valueFormat) : value;

      this.emitValue(output);
      this.$emit("change", output, value);
    },

    emitValue(value) {
      this.isCommitting = true;
      this.$emit("input", value);

      this.$nextTick(() => {
        this.isCommitting = false;
      });
    },

    clearValue() {
      this.displayValue = "";
      this.momentValue = null;
      this.errorMessage = "";
      this.pickerOpen = false;

      this.emitValue(null);
      this.$emit("change", null, null);
      this.$emit("clear");
    },

    focus() {
      const input =
        this.$refs.textInput && this.$refs.textInput.$el
          ? this.$refs.textInput.$el.querySelector("input")
          : null;

      if (input) {
        input.focus();
      }
    },
  },
};
</script>

<style scoped>
.date-input {
  width: 100%;
}

.date-input__error {
  margin-top: 5px;
  color: #f5222d;
  font-size: 12px;
  line-height: 1.4;
}

.date-input__picker {
  width: 24px;
}

.date-input__calendar-icon {
  color: rgba(0, 0, 0, 0.45);
  cursor: pointer;
}
</style>

<style>
.date-input .ant-input-affix-wrapper {
  width: 100%;
}

.date-input .date-input__picker {
  position: relative;
  display: inline-block;
  width: 22px;
  height: 22px;
}

.date-input .date-input__picker .ant-calendar-picker-input {
  position: absolute !important;
  inset: 0 !important;
  width: 22px !important;
  height: 22px !important;
  padding: 0 !important;
  border: 0 !important;
  opacity: 0 !important;
  cursor: pointer !important;
}

.date-input .date-input__picker .ant-calendar-picker-icon {
  right: 0;
  margin-top: -7px;
  pointer-events: none;
}
.ant-calendar-picker-clear {
  display: none !important;
}
</style>
